<template>
  <div class="report-container">
    <select name="size-selector" @change="seasonTextChanged" class="with-arrow no-border selector">
      <option value="" disabled selected>{{ $t('admin.exceptionReports.selectSeason') }}</option>
      <option v-for="season in seasons" :key="season" :value="season">{{ season }}</option>
    </select>
    <BackstageButton
      :variant="ButtonVariant.PRIMARY"
      :disabled="!selectedSeason || isBusy"
      @click="generateRegionalReport">
      {{ $t('admin.exceptionReports.generateRegionalReport') }}
      <BackstageIcon
        class="report-icon"
        v-if="isBusy && activeReport === ReportType.REGIONAL"
        name="loading"></BackstageIcon>
    </BackstageButton>
    <BackstageButton
      :variant="ButtonVariant.PRIMARY"
      :disabled="!selectedSeason || isBusy"
      @click="generateSeasonalReport">
      {{ $t('admin.exceptionReports.generateSeasonalReport') }}
      <BackstageIcon
        class="report-icon"
        v-if="isBusy && activeReport === ReportType.SEASONAL"
        name="loading"></BackstageIcon>
    </BackstageButton>
    <p class="seasonal-report-info">{{ $t('admin.exceptionReports.seasonalReportInfo') }}</p>
  </div>
</template>
<script setup lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import BackstageIcon from '@/components/common/BackstageIcon.vue';
import { useNotification } from '@/composables/useNotification';
import { ButtonVariant } from '@/models/button-variant.enum';
import { ExceptionService, ServiceError } from '@/services/exception.service';
import { SeasonsService } from '@/services/seasons.service';
import { ReportType, useExceptionReportStore } from '@/stores/exception-report';
import { storeToRefs } from 'pinia';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
const { notifyError } = useNotification();
const exceptionService = new ExceptionService();
const exceptionReportStore = useExceptionReportStore();

const { isBusy, activeReport } = storeToRefs(exceptionReportStore);

const seasonService = new SeasonsService();
const seasons = ref<string[]>([]);
const selectedSeason = ref<string>('');

onMounted(() => {
  fetchSeasons();
});

async function fetchSeasons() {
  const seasonsResponse = await seasonService.getSeasons(false);
  seasons.value = seasonsResponse;
}

async function generateRegionalReport() {
  if (!selectedSeason.value) {
    return;
  }

  exceptionReportStore.startReport(ReportType.REGIONAL);
  try {
    await exceptionService.getRegionalExceptionReport(selectedSeason.value);
  } catch (error) {
    console.error('Error generating regional report:', error);
  } finally {
    exceptionReportStore.finishReport();
  }
}

async function generateSeasonalReport() {
  if (!selectedSeason.value) {
    return;
  }

  exceptionReportStore.startReport(ReportType.SEASONAL);
  try {
    await exceptionService.getSeasonalExceptionReport(selectedSeason.value);
  } catch (error) {
    const message =
      error instanceof ServiceError && error.statusCode === 409
        ? t('admin.exceptionReports.reportAlreadyInProgress')
        : t('admin.exceptionReports.reportGenerationFailed');

    notifyError(message);
  } finally {
    exceptionReportStore.finishReport();
  }
}

function seasonTextChanged(event: Event) {
  selectedSeason.value = (event.target as HTMLSelectElement).value;
}
</script>
<style lang="scss" scoped>
.report-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 20px;

  .report-icon {
    margin-left: 10px;
  }

  .seasonal-report-info {
    font-size: 0.85rem;
    color: #6c757d;
    text-align: center;
    max-width: 400px;
    margin: 0;
  }
}
</style>
